import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Paper, Button, Divider } from "@mui/material";
import { Logout, DeleteForever } from "@mui/icons-material";
import { useAuth } from "@/context/AuthContext";
import { useNotification } from "@/context/NotificationContext";
import ConfirmDialog from "@/components/ConfirmDialog";

export default function Account() {
  const { t } = useTranslation();
  const { user, logout, deleteAccount } = useAuth();
  const { notify } = useNotification();
  const [showDelete, setShowDelete] = useState(false);

  const handleDelete = async () => {
    try {
      await deleteAccount();
      notify(t("account.accountDeleted"), "success");
    } catch {
      notify(t("common.error"), "error");
    } finally {
      setShowDelete(false);
    }
  };

  if (!user) return null;

  return (
    <Box>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          mb: 3,
        }}
      >
        {t("nav.account")}
      </Typography>
      <Paper sx={{ p: 4, borderRadius: 3, maxWidth: 600 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            mb: 1,
          }}
        >
          {t("account.emailLabel")}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            mb: 3,
          }}
        >
          {user.email}
        </Typography>

        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            mb: 1,
          }}
        >
          {t("account.googleIdLabel")}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            mb: 3,
          }}
        >
          {user.googleId}
        </Typography>

        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            mb: 1,
          }}
        >
          {t("account.memberSinceLabel")}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            mb: 3,
          }}
        >
          {new Date(user.createdAt).toLocaleDateString()}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Button variant="outlined" startIcon={<Logout />} onClick={logout}>
            {t("auth.signOut")}
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteForever />}
            onClick={() => setShowDelete(true)}
          >
            {t("auth.deleteAccount")}
          </Button>
        </Box>
      </Paper>
      <ConfirmDialog
        open={showDelete}
        title={t("auth.deleteAccount")}
        message={t("auth.confirmDelete")}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        onConfirm={handleDelete}
        onCancel={() => setShowDelete(false)}
      />
    </Box>
  );
}
